<template>
  <div class="ereader-wrapper">
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading your library...</p>
    </div>
    <div v-else class="library-container">
      
      <!-- Library Toolbar -->
      <div class="library-header">
        <div class="library-title-group">
          <h2 class="library-title">Library</h2>
          <span class="book-count-badge">
            {{ activeBooksCount }} {{ activeBooksCount === 1 ? 'book' : 'books' }}
          </span>
        </div>
        <div class="library-toolbar-actions">
          <button 
            class="eye-toggle-btn"
            :class="{ active: showArchived }"
            @click="toggleShowArchived"
            :title="showArchived ? 'Hide archived books' : 'Show archived books'"
            aria-label="Toggle archived books"
          >
            <!-- Eye open icon when showing archived -->
            <svg v-if="showArchived" class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            <!-- Eye closed/slashed icon when hiding archived -->
            <svg v-else class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
              <line x1="1" y1="1" x2="23" y2="23"></line>
            </svg>
            <span class="toggle-label">{{ showArchived ? 'Hide Archived' : 'Show Archived' }}</span>
            <span v-if="archivedBooksCount > 0" class="archived-pill">
              {{ archivedBooksCount }}
            </span>
          </button>
        </div>
      </div>

      <!-- Empty State: completely empty library -->
      <div v-if="totalBooksCount === 0" class="empty-state">
        <h2>Your Library is Empty</h2>
        <p>Add a book to start reading.</p>
      </div>

      <!-- Empty State: all books are archived and archived view is toggled off -->
      <div v-else-if="displayedBooks.length === 0" class="empty-state">
        <h2>All books are archived</h2>
        <p>Click "Show Archived" above to view your archived books.</p>
      </div>
      
      <!-- Book Grid -->
      <div v-else class="book-grid">
        <div 
          v-for="([key, item]) in paginatedBooks" 
          :key="key" 
          class="book-card"
          :class="{ 'is-archived': item.archived, 'is-last-read': isMostRecentRead(key) }"
        >
          <div v-if="editingRowKey === key" class="edit-overlay">
            <div class="edit-inputs">
              <input v-model="books[key].name" placeholder="Name" />
              <input v-model="books[key].url" placeholder="URL" />
              <input v-model="books[key].selector" placeholder="Selector" />
              <input v-model="books[key].forward_selector" placeholder="Forward" />
              <input v-model="books[key].back_selector" placeholder="Back" />
              <label class="archive-checkbox">
                <input type="checkbox" v-model="books[key].archived" />
                <span>Archived</span>
              </label>
            </div>
            <div class="edit-actions">
              <button class="action-btn success" @click="saveBook(key)">Save</button>
              <button class="action-btn cancel" @click="closeEdit">Cancel</button>
            </div>
          </div>
          
          <div v-else class="card-content" @click="navigateToBook(item, key)" @mouseenter="handleCardHover(item)" @touchstart.passive="handleCardHover(item)">
            <div class="book-cover" :style="{ background: getCardGradient(item.name) }">
              <div class="badge-container">
                <span v-if="item.archived" class="card-badge archived-badge">Archived</span>
                <span v-else-if="isMostRecentRead(key)" class="card-badge last-read-badge">Last Read</span>
              </div>
              <h3>{{ item.name }}</h3>
            </div>
            <div class="book-info">
              <span class="last-read">Last read: {{ formatLastRead(item.timestamp) }}</span>
            </div>
            <div class="card-actions" @click.stop>
              <button 
                class="icon-btn archive-btn" 
                @click="toggleArchive(key)" 
                :title="item.archived ? 'Unarchive Book' : 'Archive Book'"
              >
                <!-- Unarchive icon if archived -->
                <svg v-if="item.archived" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="1 4 1 10 7 10"></polyline>
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                </svg>
                <!-- Archive box icon if not archived -->
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="21 8 21 21 3 21 3 8"></polyline>
                  <rect x="1" y="3" width="22" height="5"></rect>
                  <line x1="10" y1="12" x2="14" y2="12"></line>
                </svg>
              </button>
              <button class="icon-btn edit-btn" @click="editBook(key)" title="Edit">
                <img src="../assets/icons/edit.png" alt="edit" />
              </button>
              <button class="icon-btn delete-btn" @click="deleteBook(key)" title="Delete">
                <img src="../assets/icons/trash.png" alt="trash" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination-controls" v-if="totalPages > 1">
        <button class="pill-btn" :disabled="currentPage === 1" @click="prevPage">Prev</button>
        <span class="page-indicator">{{ currentPage }} / {{ totalPages }}</span>
        <button class="pill-btn" :disabled="currentPage === totalPages" @click="nextPage">Next</button>
      </div>

      <!-- FAB for Adding -->
      <button class="fab" @click="showAddBookPopup" title="Add Book">
        <span class="fab-icon">+</span>
      </button>

      <!-- Glassmorphic Modal -->
      <div v-if="isPopupVisible" class="modal-overlay" @click.self="closeAddBookPopup">
        <div class="modal-content">
          <h2>Add a New Book</h2>
          <div class="input-group">
            <label>Name</label>
            <input v-model="name" placeholder="Enter book name" :class="{ 'error-border': formSubmitted && !name }" />
          </div>
          <div class="input-group">
            <label>URL</label>
            <input v-model="url" placeholder="https://..." :class="{ 'error-border': formSubmitted && !url }" />
          </div>
          <div class="input-group">
            <label>Content Selector</label>
            <input v-model="selector" placeholder=".post__body" />
          </div>
          <div class="input-group">
            <label>Back Selector</label>
            <input v-model="backSelector" placeholder=".post__nav-link.next" />
          </div>
          <div class="input-group">
            <label>Forward Selector</label>
            <input v-model="forwardSelector" placeholder=".post__nav-link.prev" />
          </div>
          <div class="modal-actions">
            <button class="btn-cancel" @click="closeAddBookPopup">Cancel</button>
            <button class="btn-primary" @click="handleAddBook">Save Book</button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRouter } from "vue-router";
import bookStore from "@/stores/book-store";
import userStore from "@/stores/user-store";
import { prefetchChapter } from "@/services/chapterService";
import { canPrefetch } from "@/services/prefetchScheduler";

const editingRowKey = ref(null);
const isPopupVisible = ref(false);
const formSubmitted = ref(false);
const url = ref("");
const name = ref("");
const selector = ref(".post__body");
const backSelector = ref(".post__nav-link.next");
const forwardSelector = ref(".post__nav-link.prev");
const router = useRouter();
const isLoading = ref(true);
const uid = computed(() => userStore.getters.getUser?.uid);
const books = computed(() => bookStore.getters.allBooks || {});

const showArchived = ref(
  typeof localStorage !== "undefined" &&
    localStorage.getItem("ereader_show_archived") === "true"
);

const toggleShowArchived = () => {
  showArchived.value = !showArchived.value;
  if (typeof localStorage !== "undefined") {
    localStorage.setItem("ereader_show_archived", String(showArchived.value));
  }
  currentPage.value = 1;
};

const currentPage = ref(1);
const rowsPerPage = ref(12);

const parseTimestamp = (ts) => {
  if (!ts) return 0;
  if (typeof ts === "number") return ts;
  const parsed = Date.parse(ts);
  if (!Number.isNaN(parsed)) return parsed;
  const d = new Date(ts).getTime();
  return Number.isNaN(d) ? 0 : d;
};

const formatLastRead = (ts) => {
  if (!ts) return "Never";
  const parsed = parseTimestamp(ts);
  if (!parsed) return ts;
  try {
    return new Date(parsed).toLocaleString([], {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch (_e) {
    return ts;
  }
};

// Active books: sorted with last read at the top (descending timestamp), then alphabetically by name
const sortedActiveBooks = computed(() => {
  const active = Object.entries(books.value).filter(([, item]) => !item.archived);
  return active.sort((a, b) => {
    const timeA = parseTimestamp(a[1].timestamp);
    const timeB = parseTimestamp(b[1].timestamp);
    if (timeB !== timeA) {
      return timeB - timeA; // Descending: latest first (last read at top)
    }
    const nameA = (a[1].name || "").toLowerCase();
    const nameB = (b[1].name || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });
});

// Archived books: moved to the end of the list and organized alphabetically
const sortedArchivedBooks = computed(() => {
  const archived = Object.entries(books.value).filter(([, item]) => Boolean(item.archived));
  return archived.sort((a, b) => {
    const nameA = (a[1].name || "").toLowerCase();
    const nameB = (b[1].name || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });
});

// Displayed books: active books first, followed by archived books if eye toggle is active
const displayedBooks = computed(() => {
  if (showArchived.value) {
    return [...sortedActiveBooks.value, ...sortedArchivedBooks.value];
  }
  return sortedActiveBooks.value;
});

const totalBooksCount = computed(() => Object.keys(books.value).length);
const activeBooksCount = computed(() => sortedActiveBooks.value.length);
const archivedBooksCount = computed(() => sortedArchivedBooks.value.length);

const mostRecentKey = computed(() => {
  if (sortedActiveBooks.value.length === 0) return null;
  const first = sortedActiveBooks.value[0];
  if (first && parseTimestamp(first[1].timestamp) > 0) {
    return first[0];
  }
  return null;
});

const isMostRecentRead = (key) => key === mostRecentKey.value;

const paginatedBooks = computed(() => {
  const startIndex = (currentPage.value - 1) * rowsPerPage.value;
  const endIndex = startIndex + rowsPerPage.value;
  return displayedBooks.value.slice(startIndex, endIndex);
});

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(displayedBooks.value.length / rowsPerPage.value));
});

watch(totalPages, (newTotal) => {
  if (currentPage.value > newTotal) {
    currentPage.value = Math.max(1, newTotal);
  }
});

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const handleCardHover = (item) => {
  if (item && item.url && canPrefetch()) {
    prefetchChapter(item.url, { book: item, withContent: true });
  }
};

const prefetchRecentBooksOnIdle = () => {
  const runIdlePrefetch = async () => {
    if (!canPrefetch()) return;
    const topThree = sortedActiveBooks.value.slice(0, 3);
    for (const [, bookItem] of topThree) {
      if (bookItem && bookItem.url) {
        try {
          await prefetchChapter(bookItem.url, { book: bookItem, withContent: false });
        } catch (_e) {
          // Ignore prefetch error
        }
      }
    }
  };
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(runIdlePrefetch, { timeout: 4000 });
  } else {
    setTimeout(runIdlePrefetch, 1000);
  }
};

const fetchBookData = async () => {
  if (!uid.value) return;
  if (!Object.keys(books.value).length) {
    isLoading.value = true;
  }
  try {
    await bookStore.dispatch("fetchBooks", uid.value);
    prefetchRecentBooksOnIdle();
  } catch (error) {
    console.error("Error fetching books:", error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchBookData();
});

watch(uid, (newUid) => {
  if (newUid) {
    fetchBookData();
  }
});

const trimBookFields = (book) => {
  Object.keys(book).forEach((key) => {
    if (typeof book[key] === "string") {
      book[key] = book[key].trim();
    }
  });
};

const navigateToBook = (item, key) => {
  if (editingRowKey.value !== null) return;
  router.push({
    name: "Book",
    query: { id: key },
  });
};

const editBook = (key) => {
  editingRowKey.value = key;
};

const saveBook = async (key) => {
  const updatedBook = books.value[key];
  trimBookFields(updatedBook);
  await bookStore.dispatch("updateBook", { id: key, book: updatedBook });
  closeEdit();
};

const toggleArchive = async (key) => {
  const current = books.value[key];
  if (!current) return;
  const isArchived = !current.archived;
  const updatedBook = { ...current, archived: isArchived };
  trimBookFields(updatedBook);
  await bookStore.dispatch("updateBook", { id: key, book: updatedBook });
};

const closeEdit = () => {
  editingRowKey.value = null;
};

const deleteBook = (key) => {
  const isConfirmed = window.confirm("Are you sure you want to delete this book?");
  if (isConfirmed) {
    bookStore.dispatch("deleteBook", key);
  }
};

const showAddBookPopup = () => {
  isPopupVisible.value = true;
  formSubmitted.value = false;
};

const closeAddBookPopup = () => {
  isPopupVisible.value = false;
};

const handleAddBook = () => {
  formSubmitted.value = true;
  if (!name.value || !url.value) return;

  const newBook = {
    name: name.value.trim(),
    url: url.value.trim(),
    selector: selector.value.trim(),
    back_selector: backSelector.value.trim(),
    forward_selector: forwardSelector.value.trim(),
    user_id: uid.value,
    archived: false,
  };

  bookStore.dispatch("addBook", newBook).then(() => {
    closeAddBookPopup();
    // Reset fields
    name.value = "";
    url.value = "";
  });
};

// Utils for generated covers
const getStringHash = (str) => {
  let hash = 0;
  if (!str) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; 
  }
  return hash;
};

const getCardGradient = (bookName) => {
  const hash = getStringHash(bookName);
  const h1 = Math.abs(hash) % 360;
  const h2 = (h1 + 40) % 360;
  return `linear-gradient(135deg, hsl(${h1}, 70%, 50%), hsl(${h2}, 80%, 40%))`;
};
</script>

<style scoped lang="scss">
.ereader-wrapper {
  padding: 20px;
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 10px 0;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  color: $text-color;

  .spinner {
    border: 4px solid rgba(255, 255, 255, 0.1);
    border-top: 4px solid $cyan;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin-bottom: 15px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Header & Controls */
.library-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 0 4px;
  flex-wrap: wrap;
  gap: 12px;
}

.library-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.library-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: $white;
}

.book-count-badge {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: $gray;
}

.library-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.eye-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: $text-color;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;

  .eye-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  .archived-pill {
    background: rgba(6, 182, 212, 0.2);
    color: $cyan;
    padding: 1px 7px;
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.24);
    transform: translateY(-1px);
  }

  &.active {
    background: rgba(6, 182, 212, 0.18);
    border-color: rgba(6, 182, 212, 0.45);
    color: $cyan;
    box-shadow: 0 0 12px rgba(6, 182, 212, 0.2);

    .archived-pill {
      background: $cyan;
      color: $slate;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: $text-color;
  h2 {
    font-size: 2rem;
    margin-bottom: 10px;
  }
  p {
    opacity: 0.8;
  }
}

/* Grid Layout */
.book-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  padding: 10px 0 20px;
}

/* Card Styling */
.book-card {
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, opacity 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.1);
  display: flex;
  flex-direction: column;
  min-height: 300px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.3);
    
    .card-actions {
      opacity: 1;
    }
  }

  &.is-last-read {
    border-color: rgba(6, 182, 212, 0.45);
    box-shadow: 0 4px 18px rgba(6, 182, 212, 0.15);
  }

  &.is-archived {
    opacity: 0.75;
    border: 1px dashed rgba(255, 255, 255, 0.25);

    &:hover {
      opacity: 1;
    }
  }
}

.card-content {
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.book-cover {
  position: relative;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  h3 {
    color: white;
    font-size: 1.25rem;
    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
    margin: 0;
    line-height: 1.4;
  }
}

.badge-container {
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 6px;
  z-index: 2;
}

.card-badge {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 6px;
  backdrop-filter: blur(8px);

  &.last-read-badge {
    background: rgba(6, 182, 212, 0.85);
    color: #042f2e;
    box-shadow: 0 2px 8px rgba(6, 182, 212, 0.4);
  }

  &.archived-badge {
    background: rgba(30, 41, 59, 0.85);
    color: #e2e8f0;
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
}

.book-info {
  padding: 15px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .last-read {
    font-size: 0.85rem;
    color: $text-color;
    opacity: 0.8;
  }
}

/* Floating Actions on Hover */
.card-actions {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 8px;
  opacity: 0; /* Hidden by default */
  transition: opacity 0.3s ease;
  z-index: 3;
}

.icon-btn {
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  color: white;
  
  img {
    width: 16px;
    height: 16px;
    filter: invert(1);
  }

  svg {
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    background: rgba(6, 182, 212, 0.2);
    box-shadow: 0 0 10px rgba(6, 182, 212, 0.4);
    transform: scale(1.05);
  }

  &.archive-btn:hover {
    background: rgba(139, 92, 246, 0.35);
    box-shadow: 0 0 10px rgba(139, 92, 246, 0.4);
  }

  &.delete-btn:hover {
    background: #e74c3c;
  }
}

/* Edit Mode styling within card */
.edit-overlay {
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);

  .edit-inputs {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  input {
    width: 100%;
    padding: 8px;
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 6px;
    background: rgba(0,0,0,0.3);
    color: white;
    font-size: 0.9rem;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: $cyan;
      box-shadow: 0 0 8px rgba(6, 182, 212, 0.2);
    }
  }

  .archive-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
    margin-top: 4px;
    cursor: pointer;

    input[type="checkbox"] {
      width: auto;
      accent-color: $cyan;
      cursor: pointer;
    }
  }
}

.edit-actions {
  display: flex;
  gap: 10px;
  margin-top: auto;
  .action-btn {
    flex: 1;
    padding: 8px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    &.success {
      background: rgba(6, 182, 212, 0.15);
      border: 1px solid rgba(6, 182, 212, 0.4);
      color: $cyan;
      
      &:hover {
        background: rgba(6, 182, 212, 0.25);
        box-shadow: 0 0 12px rgba(6, 182, 212, 0.3);
      }
    }
    &.cancel {
      background: transparent;
      border: 1px solid rgba(255,255,255,0.3);
      color: white;
    }
  }
}

/* Floating Action Button */
.fab {
  position: fixed;
  bottom: 40px;
  right: 40px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: $slate;
  color: white;
  border: 1px solid rgba(6, 182, 212, 0.4);
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease, background 0.3s ease, border-color 0.3s ease;
  z-index: 100;
  
  .fab-icon {
    font-size: 32px;
    line-height: 1;
    margin-top: -4px;
  }

  &:hover {
    transform: scale(1.1) rotate(90deg);
    background: lighten($slate, 10%);
    border-color: $cyan;
  }
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: rgba(30, 30, 30, 0.95);
  border: 1px solid rgba(255,255,255,0.1);
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  max-width: 450px;
  width: 90%;
  color: $text-color;

  h2 {
    margin-top: 0;
    margin-bottom: 25px;
    font-size: 1.75rem;
    color: white;
  }
}

.input-group {
  margin-bottom: 15px;
  label {
    display: block;
    font-size: 0.9rem;
    margin-bottom: 5px;
    color: rgba(255,255,255,0.8);
  }
  input {
    width: 100%;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.2);
    background: rgba(0,0,0,0.3);
    color: white;
    font-size: 1rem;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: $cyan;
    }
    &.error-border {
      border-color: #e74c3c;
    }
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;

  button {
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: background 0.2s;
  }

  .btn-cancel {
    background: transparent;
    color: rgba(255,255,255,0.7);
    &:hover {
      background: rgba(255,255,255,0.1);
    }
  }

  .btn-primary {
    background: $cyan;
    color: #042f2e;
    &:hover {
      background: $cyan-hover;
      color: white;
    }
  }
}

/* Pagination */
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 40px;
  
  .pill-btn {
    padding: 8px 24px;
    border-radius: 20px;
    border: none;
    background: $slate;
    color: white;
    font-weight: bold;
    cursor: pointer;
    transition: background 0.2s;

    &:disabled {
      background: rgba(0,0,0,0.2);
      color: rgba(255,255,255,0.3);
      cursor: not-allowed;
    }
    &:not(:disabled):hover {
      background: lighten($slate, 10%);
    }
  }

  .page-indicator {
    color: $text-color;
    font-weight: 500;
  }
}
</style>
